import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { fetchRunPreflight, NO_REJECTS_ISSUE } from '@/lib/runPreflight'
import { POLL } from '@/lib/pollTiers'
import type { Run } from '@/types/database'

/** UF-05: what is still open on the run, under Stop and Complete, before the
 *  tap. The same strings the "Complete … with open items?" dialog lists (it
 *  stays as the confirm). A warning, never a block. Pending says "Checking";
 *  a failed read says so; "Nothing open" shows only after a read that worked. */
export function RunOpenItems({ run, onShowWaiting }: { run: Run; onShowWaiting?: () => void }) {
  const live = run.status === 'ACTIVE' || run.status === 'TIPPING_COMPLETE'
  const q = useQuery({
    queryKey: ['run-preflight', run.run_id, run.version],
    enabled: live,
    refetchInterval: POLL.live,
    queryFn: () => fetchRunPreflight(supabase, run),
  })
  if (!live) return null
  if (q.isError) {
    return <p className="mt-2 text-caption text-fg-muted" data-testid="run-open-items">Could not check what is open. Try again in a moment.</p>
  }
  if (!q.data) {
    return <p className="mt-2 text-caption text-fg-muted" data-testid="run-open-items">Checking what is open…</p>
  }
  if (q.data.length === 0) {
    return <p className="mt-2 text-caption text-fg-muted" data-testid="run-open-items">Nothing open. Ready to complete.</p>
  }
  // Blocking items first; "No rejects logged yet" is a quiet note, not a line that
  // is nearly always there. Bins still to tip point at the waiting list instead of
  // wording it a second time.
  const noRejects = q.data.includes(NO_REJECTS_ISSUE)
  const blocking = q.data.filter(i => i !== NO_REJECTS_ISSUE)
  const binLines = blocking.filter(i => / still to tip$/.test(i))
  const others = blocking.filter(i => !binLines.includes(i))
  return (
    <div className="mt-2 text-caption text-fg-muted" data-testid="run-open-items">
      {blocking.length > 0 && (
        <>
          <p className="font-semibold text-fg">Open before you complete:</p>
          <ul className="list-disc pl-5">
            {binLines.length > 0 && (
              <li key="bins">
                {onShowWaiting
                  ? <button type="button" className="underline text-fg coarse:min-h-[44px]" onClick={onShowWaiting}>Bins still to tip: see Waiting to tip</button>
                  : binLines.join('; ')}
              </li>
            )}
            {others.map(i => <li key={i}>{i}</li>)}
          </ul>
        </>
      )}
      {noRejects && <p data-testid="run-open-norejects">No rejects logged yet.</p>}
    </div>
  )
}
